import {
  Children,
  isValidElement,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import MuiDivider from '@mui/material/Divider';
import MuiDrawer from '@mui/material/Drawer';
import MuiSwitch from '@mui/material/Switch';
import Box from '../primitives/Box';
import IconButton from '../primitives/IconButton';
import Typography from '../primitives/Typography';

export interface ColumnLayoutDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Defaults to common table column customize title. */
  title?: string;
  /** Quiet text action under the header, for example Ẩn tất cả. */
  actionLabel?: string;
  onAction?: () => void;
  children: ReactNode;
}

/**
 * Shared column-layout shell. The drawer does not know which columns exist;
 * the page renders one `ColumnLayoutRow` per column inside `ColumnLayoutRows`.
 */
export function ColumnLayoutDrawer({
  open,
  onClose,
  title,
  actionLabel,
  onAction,
  children,
}: ColumnLayoutDrawerProps) {
  const { t } = useTranslation('common');

  return (
    <MuiDrawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: { sx: { width: { xs: '100vw', sm: 360 } } },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }} role="presentation">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, pt: 1.5, pb: 1 }}>
          <Typography variant="h6" sx={{ flex: 1, fontWeight: 600 }}>
            {title ?? t('table.customizeColumns')}
          </Typography>
          <IconButton aria-label={t('close')} onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <MuiDivider />
        {actionLabel && onAction ? (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, py: 1 }}>
            <Box
              component="button"
              type="button"
              onClick={onAction}
              sx={{
                border: 0,
                p: 0,
                bgcolor: 'transparent',
                cursor: 'pointer',
                color: 'text.secondary',
                typography: 'body2',
                '&:hover': { color: 'text.primary' },
              }}
            >
              {actionLabel}
            </Box>
          </Box>
        ) : null}
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', pb: 2 }}>{children}</Box>
      </Box>
    </MuiDrawer>
  );
}

export interface ColumnLayoutRowsProps {
  onReorder: (activeId: string, overId: string) => void;
  children: ReactNode;
}

export function ColumnLayoutRows({ onReorder, children }: ColumnLayoutRowsProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const activeId = String(active.id);
    const overId = String(over.id);
    const ids = rowIds(children);
    if (!ids.includes(activeId) || !ids.includes(overId)) return;
    onReorder(activeId, overId);
  };

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>{children}</Box>
    </DndContext>
  );
}

export interface ColumnLayoutRowProps {
  id: string;
  label: string;
  visible: boolean;
  disabled?: boolean;
  onToggle: () => void;
  /** Control after the label, before the visibility switch. */
  trailing?: ReactNode;
  /** Nested row under a parent in the same drawer. */
  inset?: boolean;
}

export function ColumnLayoutRow({
  id,
  label,
  visible,
  disabled = false,
  onToggle,
  trailing,
  inset = false,
}: ColumnLayoutRowProps) {
  const { t } = useTranslation('common');
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id,
    disabled,
  });
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id, disabled });

  const mergeRef = (node: HTMLElement | null): void => {
    setNodeRef(node);
    setDropRef(node);
  };

  return (
    <Box
      ref={mergeRef}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        minHeight: 44,
        pl: inset ? 5 : 2,
        pr: 2,
        bgcolor: isOver || isDragging ? 'action.hover' : 'transparent',
        opacity: isDragging ? 0.85 : 1,
        transform: transform ? CSS.Translate.toString(transform) : undefined,
      }}
    >
      <Box
        {...attributes}
        {...listeners}
        aria-label={t('table.dragColumn')}
        sx={{
          display: 'flex',
          color: 'text.disabled',
          cursor: disabled ? 'default' : 'grab',
          touchAction: 'none',
        }}
      >
        <DragIndicatorIcon fontSize="small" />
      </Box>
      <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
        {label}
      </Typography>
      {trailing}
      <MuiSwitch
        size="small"
        checked={visible}
        disabled={disabled}
        onChange={onToggle}
        slotProps={{ input: { 'aria-label': label } }}
      />
    </Box>
  );
}

function rowIds(children: ReactNode): string[] {
  const ids: string[] = [];
  const visit = (nodes: ReactNode): void => {
    Children.forEach(nodes, (child) => {
      if (!isValidElement(child)) return;
      const props = child.props as { id?: unknown; children?: ReactNode };
      if (typeof props.id === 'string') {
        ids.push(props.id);
        return;
      }
      if (props.children) visit(props.children);
    });
  };
  visit(children);
  return ids;
}
